import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  getDictationAddonNotes,
  uploadDictationAddonNote,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import useRecorder from "components/customHooks/useRecorderMp3";
import MicIcon from "@mui/icons-material/Mic";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { DataGridCustomPagination } from "components/helpers";
import { columns } from "./helper";
import { PauseCircleOutline, StopCircleOutlined } from "@mui/icons-material";

const AddOnNotes = ({ dictation_id, practice_id, npi }) => {
  const [notes, setNotes] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [time, setTime] = useState({
    sec: 0,
    min: 0,
    hr: 0,
  });
  const [count, setCount] = useState(null);
  const [countdown, setCountdown] = useState("");
  const [intervalId, setIntervalId] = useState();
  const [inputMode, setInputMode] = useState("text");
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const userData = useUserData();

  const [
    audioURL,
    isRecording,
    startRecording,
    stopRecording,
    audioFile,
    audioError,
    togglePause,
    isPaused,
    setAudioURL,
  ] = useRecorder();

  useEffect(() => {
    if (isRecording && !isPaused) {
      const id = setInterval(() => {
        setTime((prev) => {
          let newTime = { ...prev };
          if (newTime.sec < 59) newTime.sec += 1;
          else {
            newTime.min += 1;
            newTime.sec = 0;
          }
          if (newTime.min === 60) {
            newTime.min = 0;
            newTime.hr += 1;
          }
          return newTime;
        });
      }, 1000);
      setIntervalId(id);
    } else if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isRecording, isPaused]);

  useEffect(() => {
    if (count === 0) {
      clearTimeout(countdown);
      startRecording();
      setCount(null);
    }
  }, [count]);

  const token = userData?.access_token;

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const payload = {
        type: "Dictation",
        search_criteria: {
          practice_id,
          dictation_id,
        },
      };
      const res = await getDictationAddonNotes(payload, token);
      if (res) {
        setNotes(res);
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setNoteText("");
    setAudioURL(null);
    if (isRecording) stopRecording();
  };

  const handleSave = async () => {
    try {
      const payload = new FormData();
      if (inputMode === "text") {
        payload.append(
          "request_json",
          JSON.stringify({
            type: "Dictation",
            practice_id,
            dictation_id,
            note_text: noteText,
            user_name: userData.first_name + " " + userData.last_name,
          })
        );
      } else if (inputMode === "audio") {
        payload.append("file", audioFile);
        payload.append(
          "request_json",
          JSON.stringify({
            type: "Dictation",
            practice_id,
            dictation_id,
            user_name: userData.first_name + " " + userData.last_name,
          })
        );
      }

      const res = await uploadDictationAddonNote(payload, token);
      if (res && res.status === "Success") {
        setSnackbarSeverity("success");
        setSnackbarMessage("Note added successfully");
        setSnackbarOpen(true);
        fetchNotes();
        handleClose();
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to add note");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Error adding note");
      setSnackbarOpen(true);
      console.log("error: ", error);
    }
  };

  const stopAudioRecording = async () => {
    stopRecording();
  };

  const initiateRecording = async () => {
    setCount(3);
    clearInterval(intervalId);
    setTime({
      sec: 0,
      min: 0,
      hr: 0,
    });
    setCountdown(
      setInterval(() => {
        setCount((prev) => {
          if (prev > 0) return prev - 1;
        });
      }, 1000)
    );
  };

  return (
    <Box bgcolor={"white"} borderRadius={2} padding={2} mt={1.5}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        mb={2}
      >
        <Typography fontWeight={600}>Add On Notes</Typography>
        <Button
          variant="text"
          sx={{ color: (theme) => theme.palette.primary.main }}
          onClick={handleOpen}
        >
          + Add Notes
        </Button>
      </Box>

      {notes.length === 0 ? (
        <Typography variant="body1" color="info" align="center" mt={2}>
          No Notes Found!
        </Typography>
      ) : (
        <DataGrid
          columns={columns(currentPlaying, setCurrentPlaying, practice_id, npi)}
          rows={notes}
          slots={{ pagination: DataGridCustomPagination, toolbar: GridToolbar }}
          loading={loading}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
        />
      )}

      <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Notes</DialogTitle>
        <DialogContent>
          <RadioGroup
            row
            value={inputMode}
            onChange={(e) => setInputMode(e.target.value)}
            sx={{ mb: 2, gap: 2 }}
          >
            <FormControlLabel value="text" control={<Radio />} label="Text" />
            <FormControlLabel value="audio" control={<Radio />} label="Audio" />
          </RadioGroup>

          {inputMode === "text" && (
            <TextField
              multiline
              minRows={4}
              fullWidth
              variant="outlined"
              label="Enter notes here"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
          )}

          {inputMode === "audio" && (
            <Box display={"flex"} alignItems={"center"} mb={2} gap={2}>
              {audioError ? (
                <PlayCircleOutlineIcon fontSize="16" />
              ) : isRecording ? (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <IconButton
                    onClick={stopAudioRecording}
                    sx={{
                      bgcolor: "secondary.main",
                      color: "white",

                      "&:hover": {
                        bgcolor: "secondary.dark",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    <StopCircleOutlined fontSize="medium" />
                  </IconButton>

                  <IconButton
                    onClick={togglePause}
                    sx={{
                      bgcolor: isPaused ? "grey.300" : "secondary.light",
                      color: isPaused ? "grey.600" : "secondary.dark",

                      "&:hover": {
                        bgcolor: isPaused ? "grey.400" : "secondary.main",
                        color: "white",
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {isPaused ? (
                      <PlayCircleOutlineIcon fontSize="medium" />
                    ) : (
                      <PauseCircleOutline fontSize="medium" />
                    )}
                  </IconButton>
                </Box>
              ) : count && count !== 0 ? (
                <Avatar sx={{ bgcolor: "#D91A5F" }}>{count}</Avatar>
              ) : (
                <MicIcon
                  color="secondary"
                  onClick={initiateRecording}
                  fontSize="large"
                  sx={{ cursor: "pointer" }}
                />
              )}
              <Typography variant="h6" sx={{ minWidth: 90 }}>
                {`${time.min < 10 ? "0" : ""}${time.min} : ${
                  time.sec < 10 ? "0" : ""
                }${time.sec}`}
              </Typography>
              {audioURL && (
                <audio
                  src={audioURL}
                  controls
                  style={{ marginLeft: 16, flexGrow: 1 }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isRecording} color="inherit">
            Cancel
          </Button>

          <button
            className={"blank-hover-btn"}
            disabled={!noteText && !audioFile}
            onClick={handleSave}
          >
            Save
          </button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddOnNotes;
